
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Check, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { formatCurrency } from '@/utils/estimates/formatCurrency';

interface ComparisonStatusAlertProps {
  isIdentical: boolean;
  differenceCount: number;
  priceDifference: number;
  summaryChanges?: number;
}

export const ComparisonStatusAlert: React.FC<ComparisonStatusAlertProps> = ({ 
  isIdentical, 
  differenceCount,
  priceDifference,
  summaryChanges = 0
}) => {
  if (isIdentical) {
    return (
      <Alert className="border-green-500 bg-green-50">
        <Check className="h-5 w-5 text-green-600" />
        <AlertTitle className="text-green-700 font-medium">Estimates are identical</AlertTitle>
        <AlertDescription className="text-green-700">
          No differences were found between the estimates.
        </AlertDescription>
      </Alert>
    );
  }

  const isPriceIncrease = priceDifference > 0;
  const lineItemChanges = differenceCount - summaryChanges;
  
  // Using amber/yellow for all differences (regardless of price direction)
  return (
    <Alert className="border-amber-500 bg-amber-50">
      {isPriceIncrease ? (
        <ArrowUp className="h-5 w-5 text-amber-600" />
      ) : priceDifference < 0 ? (
        <ArrowDown className="h-5 w-5 text-amber-600" />
      ) : (
        <AlertCircle className="h-5 w-5 text-amber-600" />
      )}
      <AlertTitle className="text-amber-700 font-medium">
        Differences detected
      </AlertTitle>
      <AlertDescription className="text-amber-700">
        {differenceCount > 0 ? (
          <>
            {lineItemChanges > 0 && `${lineItemChanges} line item ${lineItemChanges === 1 ? 'difference' : 'differences'} found. `}
            {summaryChanges > 0 && `${summaryChanges} summary ${summaryChanges === 1 ? 'difference' : 'differences'} detected. `}
            {priceDifference !== 0 && (
              <span className="font-medium">
                Total price {isPriceIncrease ? 'increased' : 'decreased'} by {formatCurrency(Math.abs(priceDifference))}.
              </span>
            )}
          </>
        ) : priceDifference !== 0 ? (
          <span>
            Only price differences found. Total price {isPriceIncrease ? 'increased' : 'decreased'} by {formatCurrency(Math.abs(priceDifference))}.
          </span>
        ) : (
          <span>Minor differences detected that don't affect pricing.</span>
        )}
      </AlertDescription>
    </Alert>
  );
};
